import { useMemo } from 'react'
import { useQueries } from '@tanstack/react-query'
import { fetchTransactions } from '@/lib/api'
import { shiftMonth, filterByScope, filterByExpenseType, filterByPaidBy } from '@/lib/utils'
import { UNCATEGORIZED_LABEL } from '@/lib/constants'
import type { ScopeFilter, ExpenseTypeFilter, PaidByFilter } from '@/lib/utils'
import type { Transaction } from './use-transactions'
import { useScope } from '@/lib/scope-context'

export type TrendData = {
  month: string
  label: string
  total: number
  [category: string]: string | number
}

export function getLast6Months(yearMonth: string): string[] {
  return Array.from({ length: 6 }, (_, i) => shiftMonth(yearMonth, -(5 - i)))
}

export function aggregateTrendData(
  rawResults: Array<{ month: string; transactions: Transaction[] }>,
  scopeFilter: ScopeFilter,
  expenseType: ExpenseTypeFilter,
): { trendData: TrendData[]; trendCategories: string[] } {
  const allCats = new Set<string>()
  const trend: TrendData[] = rawResults.map((r) => {
    const [, mon] = r.month.split("-")
    const scopeFiltered = filterByScope(r.transactions, scopeFilter)
    const filtered = filterByExpenseType(scopeFiltered, expenseType)
    const categoryAmounts: Record<string, number> = {}
    for (const tx of filtered) {
      for (const item of tx.items) {
        const cat = item.parent_category_name ?? UNCATEGORIZED_LABEL
        categoryAmounts[cat] = (categoryAmounts[cat] ?? 0) + item.amount
        allCats.add(cat)
      }
    }
    const total = filtered.reduce(
      (sum: number, tx: Transaction) => sum + tx.total_amount, 0
    )
    return { month: r.month, label: `${parseInt(mon)}月`, total, ...categoryAmounts }
  })

  const catTotals = new Map<string, number>()
  for (const t of trend) {
    for (const cat of allCats) {
      catTotals.set(cat, (catTotals.get(cat) ?? 0) + (Number(t[cat]) || 0))
    }
  }
  const sorted = Array.from(allCats).sort(
    (a, b) => (catTotals.get(b) ?? 0) - (catTotals.get(a) ?? 0)
  )
  return { trendData: trend, trendCategories: sorted }
}

export function useTrendData(
  month: string,
  scopeOverride?: ScopeFilter,
  expenseType: ExpenseTypeFilter = 'all',
  paidByOverride: PaidByFilter = 'all',
) {
  const { scope } = useScope()
  const effectiveScope = scopeOverride ?? scope
  const trendMonths = useMemo(() => getLast6Months(month), [month])

  // Fetch all transactions per month — shares cache with useTransactions
  const queries = useQueries({
    queries: trendMonths.map((m) => ({
      queryKey: ['transactions', m, 'all'],
      queryFn: () => fetchTransactions(m),
    })),
  })

  const isLoading = queries.some((q) => q.isLoading)

  // Filter & aggregate client-side — no refetch on scope/expenseType change
  const { trendData, trendCategories, monthlyTransactions } = useMemo(() => {
    if (isLoading) {
      return {
        trendData: [] as TrendData[],
        trendCategories: [] as string[],
        monthlyTransactions: [] as Transaction[][],
      }
    }
    const rawResults = trendMonths.map((m, i) => ({
      month: m,
      transactions: (queries[i].data ?? []) as Transaction[],
    }))
    const aggregated = aggregateTrendData(rawResults, effectiveScope, expenseType)
    const monthly = rawResults.map((r) =>
      filterByPaidBy(filterByExpenseType(filterByScope(r.transactions, effectiveScope), expenseType), paidByOverride),
    )
    return { ...aggregated, monthlyTransactions: monthly }
  }, [isLoading, queries, trendMonths, effectiveScope, expenseType, paidByOverride])

  return { trendData, trendCategories, monthlyTransactions, isLoading }
}
